import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, fmtMoney } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { trackOfferSubmitted } from "@/lib/analytics";

export default function OfferDialog({ note, open, onOpenChange, onSubmitted }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (note?.asking_price) setAmount(String(Math.round(note.asking_price * 0.92)));
  }, [note]);

  const submit = async () => {
    if (!user) {
      toast.info("Please sign in to submit an offer");
      onOpenChange(false);
      navigate("/login");
      return;
    }
    if (user.role === "admin") {
      toast.error("Admin accounts cannot submit offers");
      return;
    }
    const numeric = Number(amount);
    if (!numeric || numeric <= 0) {
      toast.error("Enter a valid offer amount");
      return;
    }
    setBusy(true);
    try {
      await api.post("/offers", { note_id: note.note_id, offer_amount: numeric, message });
      trackOfferSubmitted(note, numeric);
      toast.success("Offer submitted");
      onSubmitted?.();
      onOpenChange(false);
      setMessage("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit offer");
    } finally {
      setBusy(false);
    }
  };

  if (!note) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="offer-dialog" className="rounded-none border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Submit Offer</DialogTitle>
          <DialogDescription>
            {note.vehicle_year} {note.vehicle_make} {note.vehicle_model} · Asking {fmtMoney(note.asking_price)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="amount" className="font-overline">Offer Amount (USD)</Label>
            <Input id="amount" data-testid="offer-amount-input" type="number" min="1"
                   className="rounded-none mt-2 font-mono-data text-lg" value={amount}
                   onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="msg" className="font-overline">Message (optional)</Label>
            <Textarea id="msg" data-testid="offer-message-input" rows={3} className="rounded-none mt-2"
                      value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-none uppercase text-xs tracking-wider"
                  onClick={() => onOpenChange(false)} data-testid="offer-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-none uppercase text-xs tracking-wider"
                  data-testid="offer-submit">{busy ? "Submitting…" : "Submit Offer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
